import { useEffect, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { FiArrowLeft } from 'react-icons/fi';
import { BrandMark } from '@/components/common/BrandMark';

export const AUTH_FIELD =
  'w-full rounded-xl border-0 bg-white px-3.5 py-3 font-[inherit] text-[14.5px] text-[var(--ink)] shadow-[0_1px_2px_rgba(20,10,5,.06)] outline-none ring-1 ring-black/5 placeholder:text-[#9aa0a8] transition focus:ring-2 focus:ring-[var(--ember)]/70';
export const AUTH_LABEL = 'mb-1 block text-[12.5px] font-semibold tracking-wide text-white/92';
export const AUTH_PRIMARY =
  'inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border-0 bg-[linear-gradient(135deg,var(--ember),var(--cobalt))] px-6 py-3.5 text-[15px] font-bold text-white shadow-[0_14px_32px_rgba(244,80,42,.4)] transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_rgba(244,80,42,.48)] active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60';
export const AUTH_EYE =
  'absolute top-1/2 right-2.5 grid size-8 -translate-y-1/2 place-items-center rounded-lg border-0 bg-transparent text-[#7a7f88] transition hover:bg-[#f3f3f3] hover:text-[var(--ink)]';
export const AUTH_LINK = 'font-semibold text-white no-underline underline-offset-2 hover:underline';

export function AuthField({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: ReactNode;
}) {
  return (
    <div className="mb-3">
      <label className={AUTH_LABEL} htmlFor={htmlFor}>
        {label}
      </label>
      {children}
    </div>
  );
}

export function AuthShell({
  title,
  children,
  footer,
  backTo,
}: {
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Fallback when browser history is empty */
  backTo?: string;
}) {
  const navigate = useNavigate();

  useEffect(() => {
    // One scrollport only: lock document scroll so AuthShell's overflow is the sole bar.
    const { body, documentElement: html } = document;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyBg = body.style.background;
    const prevHtmlGutter = html.style.scrollbarGutter;
    body.style.overflow = 'hidden';
    body.style.background = '#140c0a';
    html.style.scrollbarGutter = 'auto';
    return () => {
      body.style.overflow = prevBodyOverflow;
      body.style.background = prevBodyBg;
      html.style.scrollbarGutter = prevHtmlGutter;
    };
  }, []);

  function goBack() {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(backTo ?? '/', { replace: true });
  }

  return (
    <div
      className="fixed inset-0 z-40 overflow-x-hidden overflow-y-auto overscroll-contain bg-[#140c0a]"
      data-testid="auth-page"
    >
      {/* Viewport-pinned backdrop — absolute-on-content left a white gap when the form was taller than the screen. */}
      <div
        className="pointer-events-none fixed inset-0"
        style={{
          background:
            'linear-gradient(125deg, #140c0a 0%, #2a1410 28%, #8a2e18 55%, #f4502a 78%, #ff9a62 100%)',
        }}
        aria-hidden
      />
      <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden>
        <div className="absolute top-[-18%] left-[-18%] h-[70vmax] w-[42vmax] rotate-[-28deg] rounded-[50%] bg-[linear-gradient(180deg,rgba(255,210,170,.55)_0%,rgba(244,80,42,.15)_55%,transparent_100%)] blur-[48px]" />
        <div className="absolute right-[-12%] bottom-[-22%] h-[65vmax] w-[48vmax] rotate-[18deg] rounded-[48%] bg-[linear-gradient(200deg,rgba(255,140,90,.7)_0%,rgba(201,61,28,.35)_45%,transparent_100%)] blur-[56px]" />
        <div className="absolute top-[22%] left-[38%] h-[36vmax] w-[28vmax] rotate-12 rounded-full bg-[radial-gradient(circle,rgba(255,255,255,.22)_0%,transparent_68%)] blur-[40px]" />
      </div>

      <div className="relative z-10 flex min-h-full justify-center px-4 py-5 sm:py-8">
        <div className="my-auto flex w-full max-w-[420px] flex-col gap-2.5">
          <button
            type="button"
            onClick={goBack}
            className="inline-flex w-fit cursor-pointer items-center gap-2 rounded-full border border-white/30 bg-white/12 px-4 py-2 text-[13.5px] font-semibold text-white shadow-[0_8px_24px_rgba(20,10,5,.18)] backdrop-blur-md transition hover:bg-white/22"
            data-testid="auth-back"
          >
            <FiArrowLeft className="size-4" aria-hidden />
            Back
          </button>

          <div className="relative rounded-[28px] border border-white/40 bg-white/[0.16] p-6 shadow-[0_28px_80px_rgba(20,10,5,.38)] backdrop-blur-[28px] sm:p-8">
            <div
              className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/70 to-transparent"
              aria-hidden
            />
            <div
              className="pointer-events-none absolute -top-24 left-1/2 h-40 w-[120%] -translate-x-1/2 rounded-full bg-white/10 blur-2xl"
              aria-hidden
            />

            <div className="relative mb-5 flex flex-col items-start gap-3.5">
              <Link to="/" className="inline-flex items-center gap-2.5 no-underline transition hover:opacity-95">
                <span className="grid size-11 place-items-center rounded-[14px] bg-[linear-gradient(135deg,var(--ember),var(--cobalt))] text-white shadow-[0_10px_28px_rgba(244,80,42,.45)] ring-1 ring-white/25">
                  <BrandMark className="size-[17px]" />
                </span>
                <span className="font-['Fraunces',Georgia,serif] text-[17px] font-bold tracking-tight text-white drop-shadow-sm">
                  MAKTech Academy
                </span>
              </Link>
              <h1 className="font-['Fraunces',Georgia,serif] text-[32px] leading-none font-bold tracking-tight text-white drop-shadow-sm sm:text-[36px]">
                {title}
              </h1>
            </div>

            <div className="relative">{children}</div>

            {footer ? (
              <p className="relative mt-5 text-center text-[13.5px] text-white/82">{footer}</p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export function isStrongPassword(password: string) {
  return password.length >= 8 && /[a-zA-Z]/.test(password) && /\d/.test(password);
}
